import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { ProjectRole, SystemRole } from '@/core/types/models/RoleTypes';
import type { UiProject } from '@/shared/mappers';
import {
  type NavigationGroup,
  type NavigationItem,
  type NavigationVisibilityContext,
  findNavigationItemById,
  findNavigationItemByPath,
  getStandaloneNavigationItems,
  getVisibleNavigationItems,
  groupNavigationItems,
  searchNavigationItems,
} from '@/shared/config/navigation';
import { useAuth } from '@/shared/composables/useAuth';
import { useUserRole } from '@/shared/composables/useUserRole';
import { useProjectStore } from '@/shared/stores/project';
import { useRecentNavigationStore } from '@/shared/stores/recentNavigation';
import { isProjectViewerRole } from '@/shared/utils/projectAccess';

export interface UseNavigationMenuReturn {
  currentNavItem: ComputedRef<NavigationItem | undefined>;
  currentProject: ComputedRef<UiProject | null>;
  displayedBottomStandaloneItems: ComputedRef<NavigationItem[]>;
  displayedGroups: ComputedRef<NavigationGroup[]>;
  displayedTopStandaloneItems: ComputedRef<NavigationItem[]>;
  hasSearchQuery: ComputedRef<boolean>;
  hasSelectedProject: ComputedRef<boolean>;
  isSystemAdmin: ComputedRef<boolean>;
  isProjectOwner: ComputedRef<boolean>;
  recentItems: ComputedRef<NavigationItem[]>;
  searchQuery: Ref<string>;
  visibleItems: ComputedRef<NavigationItem[]>;
  visibilityContext: ComputedRef<NavigationVisibilityContext>;
  clearSearch: () => void;
}

export function useNavigationMenu(): UseNavigationMenuReturn {
  const route = useRoute();
  const authStore = useAuth();
  const { userRole } = useUserRole();
  const projectStore = useProjectStore();
  const recentNavigationStore = useRecentNavigationStore();
  const searchQuery = ref('');

  const isSystemAdmin = computed(
    () => authStore.user?.value?.systemRole === SystemRole.Administrator,
  );
  const isProjectViewer = computed(() => isProjectViewerRole(userRole.value));
  const isProjectOwner = computed(
    () => isSystemAdmin.value || userRole.value === ProjectRole.ProjectOwner,
  );
  const hasSelectedProject = computed(() => Boolean(projectStore.selectedProjectId));

  const visibilityContext = computed<NavigationVisibilityContext>(() => ({
    hasSelectedProject: hasSelectedProject.value,
    isSystemAdmin: isSystemAdmin.value,
    isProjectViewer: isProjectViewer.value,
    isProjectOwner: isProjectOwner.value,
  }));

  const visibleItems = computed(() => getVisibleNavigationItems(visibilityContext.value));
  const currentNavItem = computed(() => {
    // Prefer returnTo-based context when available (e.g., session page reached from commit history)
    const returnTo = route.query.returnTo;
    if (typeof returnTo === 'string' && returnTo) {
      const contextItem = findNavigationItemByPath(returnTo);
      if (contextItem) {
        return contextItem;
      }
    }
    return findNavigationItemByPath(route.path);
  });
  const currentProject = computed(
    () => projectStore.getProjectById(projectStore.selectedProjectId) ?? null,
  );
  const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0);
  const filteredItems = computed(() =>
    hasSearchQuery.value
      ? searchNavigationItems(searchQuery.value, visibleItems.value)
      : visibleItems.value,
  );
  const displayedTopStandaloneItems = computed(() =>
    getStandaloneNavigationItems(filteredItems.value, 'top'),
  );
  const displayedBottomStandaloneItems = computed(() =>
    getStandaloneNavigationItems(filteredItems.value, 'bottom'),
  );

  const displayedGroups = computed(() => groupNavigationItems(filteredItems.value));

  const recentItems = computed(() =>
    recentNavigationStore.itemIds
      .map((itemId) => findNavigationItemById(itemId))
      .filter((item): item is NavigationItem => item !== undefined)
      .filter((item) => visibleItems.value.some((visibleItem) => visibleItem.id === item.id)),
  );

  function clearSearch(): void {
    searchQuery.value = '';
  }

  return {
    currentNavItem,
    currentProject,
    displayedBottomStandaloneItems,
    displayedGroups,
    displayedTopStandaloneItems,
    hasSearchQuery,
    hasSelectedProject,
    isSystemAdmin,
    isProjectOwner,
    recentItems,
    searchQuery,
    visibleItems,
    visibilityContext,
    clearSearch,
  };
}
